import React, { useState } from "react";
import { Button, Icon, Link } from "@amzn/awsui-components-react/polaris";
import { searchPorAsinsNoLimit } from "../app/api";
import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../server/asin-central/model";
import {
  COLUMN_DEFINITIONS,
  formattedRestrictedPorAsinProgramName,
} from "../porAsinList/PorAsinListTable-config";
import { serviceToDisplay } from "../../util/dateUtils";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/rootReducer";
import {
  ERROR_NOTIF_ITEMS,
  IN_PROGRESS_NOTIF_ITEMS,
} from "../notification/notification-config";
import {
  addNotification,
  clearNotifications,
  setNotification,
} from "../notification/notificationConnector";
import { exportCSV, getTimestampedCsvFileName } from "../../util/fileUtils";
import {
  ValidationAttribute,
  ValidationAttributeDetailStep,
} from "../validation/validationAttribute";
import { getValueFromUrlParameter, TAB_ID_KEY } from "../../util/urlUtils";
import { ValidationServiceMetadata } from "./porExport-Config";
import { Feature, trackButtonClick } from "../telemetry/telemetry";

const stepNameSet = new Set<string>();
let AsinDetailMap = new Map<string, string>();

function getAsinDetailKey(asin: FullAsinWithAudits, stepName: string) {
  return `${asin.asin}.${asin.marketplace}.${stepName}`;
}

interface ExportButtonProps {
  componentName: Feature;
}

export const ExportButton = ({ componentName }: ExportButtonProps) => {
  const dispatch = useDispatch();
  const [exportInProgress, setExportInProgress] = useState<boolean>(false);
  const { queryString } = useSelector(
    (state: RootState) => state.generateQuery
  );

  function getColumnIndexForAttribute(columns: string[], attribute: string) {
    return columns.indexOf(attribute);
  }

  function getValidationStepValuesForExport(
    asin: FullAsinWithAudits | RestrictedAsin
  ) {
    return [...stepNameSet.values()].map((stepName: string) =>
      asin.isRestricted
        ? ""
        : AsinDetailMap.get(getAsinDetailKey(asin, stepName))
    );
  }

  /**
   * Do any column-specific manipulations.
   */
  function porExportTransform(csvData: [string[]]) {
    const validationStatusSummaryIndex = getColumnIndexForAttribute(
      csvData[0],
      "Validation Status Summary"
    );
    const transformValidationStatusSummary = (validationStatus: string) => {
      if (!validationStatus || typeof validationStatus !== "string") {
        return "";
      }
      const validations = validationStatus.split(",").map((str) => str.trim());
      const denominator = validations.length;
      const numerator = validations.filter((str) =>
        str.includes("PASS")
      ).length;
      return ((numerator / denominator) * 100).toFixed(0).toString() + "%";
    };

    for (let i = 1; i < csvData.length; i++) {
      csvData[i][validationStatusSummaryIndex] =
        transformValidationStatusSummary(
          csvData[i][validationStatusSummaryIndex]
        );
    }
    return csvData;
  }

  function porExportMapper(
    asin: FullAsinWithAudits | RestrictedAsin,
    includeValidationData: boolean
  ) {
    const validationData = includeValidationData
      ? getValidationStepValuesForExport(asin)
      : [];

    const asinParsed = JSON.parse(JSON.stringify(asin));
    // Clear bundle description formatting
    if (
      asinParsed["description"] &&
      ["soft_bundle", "vmp_bundle"].includes(asinParsed["asinType"])
    ) {
      asinParsed["description"] = clearBundleDescriptionFormatting(
        asinParsed["description"]
      );
    }
    if (asinParsed["bundleComponentQuantity"]) {
      asinParsed["bundleComponentQuantity"] = mapToString(
        asinParsed["bundleComponentQuantity"]
      );
    }
    const exportEntry = COLUMN_DEFINITIONS.map(
      (col: any) => asinParsed[col.id!]
    ).concat(validationData);

    exportEntry[0] = exportEntry[0]
      ? serviceToDisplay(exportEntry[0])
      : exportEntry[0];
    exportEntry[1] = exportEntry[1]
      ? serviceToDisplay(exportEntry[1])
      : exportEntry[1];
    exportEntry[2] = asin.isRestricted
      ? formattedRestrictedPorAsinProgramName(asin)
      : exportEntry[2];
    return exportEntry;
  }

  // This function pulls the steps from the detail results.
  function getStepNames(
    asin: FullAsinWithAudits,
    attributeName: string
  ): ValidationAttributeDetailStep[] {
    const detailAttributeValue = asin[
      attributeName as keyof FullAsinWithAudits
    ] as string;

    if (!detailAttributeValue) {
      return [];
    }

    try {
      // DLCS returns this as an escaped string.
      const unescapedAttribute = unescape(detailAttributeValue);
      const detailResults = JSON.parse(
        unescapedAttribute
      ) as ValidationAttribute;

      return detailResults.detail;
    } catch (e: any) {
      return [];
    }
  }

  /*
    This function constructs a set of each step attribute for each service.
    For example: Hattrick.Registration.Reason
    It also creates a map of {asin}.{serviceName}.{stepName}.{Result/Reason/Hint} to value.
    This value is used when constructing the CSV.
   */
  function buildAsinStepNameMap(asins: FullAsinWithAudits[]) {
    ValidationServiceMetadata.forEach((validationResultType) =>
      asins.forEach((asin) => {
        getStepNames(asin, validationResultType.attributeName)?.forEach(
          (step) => {
            if (Object.prototype.hasOwnProperty.call(asin, "asin")) {
              [
                { label: "Result", value: step.result },
                { label: "Reason", value: step.reason },
                { label: "More_Information", value: step.hint },
              ].forEach((stepAttribute) => {
                const stepName = `${validationResultType.serviceName}.${step.step}.${stepAttribute.label}`;
                AsinDetailMap = AsinDetailMap.set(
                  getAsinDetailKey(asin, stepName),
                  stepAttribute.value ?? ""
                );
                stepNameSet.add(stepName);
              });
            }
          }
        );
      })
    );
  }

  function clearBundleDescriptionFormatting(val: string) {
    const emRegex = /\*(.*?)\*/g;
    return val.replace(emRegex, (_, text) => text);
  }

  function mapToString(val: Record<string, number>): string {
    return Object.entries(val)
      .map(([key, value]) => `${key}: ${value}`)
      .join(", ");
  }

  function clearData() {
    stepNameSet.clear();
    AsinDetailMap.clear();
  }

  async function getCsvExportData() {
    dispatch(clearNotifications());
    dispatch(addNotification(IN_PROGRESS_NOTIF_ITEMS("Exporting ASINs")));
    searchPorAsinsNoLimit(queryString)
      .then((asins: any) => {
        const includeValidationData =
          getValueFromUrlParameter(TAB_ID_KEY, "") === "validation";
        let validationHeaderData: string[] = [];
        if (includeValidationData) {
          buildAsinStepNameMap(asins);
          validationHeaderData = [...stepNameSet.values()];
        }

        const csvData = [
          //todo:  add obfuscated MP ID, program ID?
          COLUMN_DEFINITIONS.map((column: any) => column.header).concat(
            validationHeaderData
          ),
        ].concat(
          asins.map((asin: any) => porExportMapper(asin, includeValidationData))
        ) as [[]];

        dispatch(
          setNotification({
            type: "info",
            dismissible: true,
            header: (
              <div>
                Please download exported data{" "}
                <Link
                  color={"inverted"}
                  children="here"
                  onFollow={(event) => {
                    exportCSV({
                      fileName: getTimestampedCsvFileName("porExport"),
                      csvData: porExportTransform(csvData),
                    });
                  }}
                />
                .
              </div>
            ),
          })
        );
        setExportInProgress(false);
      })
      .catch((err: any) => {
        dispatch(
          setNotification(ERROR_NOTIF_ITEMS("Failed to export: " + err))
        );
        setExportInProgress(false);
      });
    clearData();
  }

  return (
    <div>
      <Button
        onClick={(event) => {
          trackButtonClick(componentName, "export", {});
          setExportInProgress(true);
          getCsvExportData();
        }}
        disabled={exportInProgress}
      >
        <Icon name="download" size="normal" variant="normal" /> Export
      </Button>
    </div>
  );
};
